import axios, { AxiosInstance } from "axios";
import { authStore } from "../stores/main";

type KycEnvelope = {
  status?: string;
  code?: number;
  msg?: string;
  data?: Record<string, unknown>;
  response?: Record<string, unknown>;
};

function appendAuth(params: URLSearchParams) {
  if (!authStore.isAuthenticated) return;
  const { userid, token } = authStore.userIdAndToken;
  params.append("user_id", userid);
  params.append("session_token", token);
}

function readTopMsg(body: unknown): string {
  if (body && typeof body === "object" && "msg" in body) {
    const m = (body as { msg?: unknown }).msg;
    if (typeof m === "string" && m.trim()) return m.trim();
  }
  return "";
}

function extractResponsePayload(body: unknown): Record<string, unknown> | undefined {
  if (!body || typeof body !== "object") return undefined;
  const b = body as Record<string, unknown>;
  if (b.response && typeof b.response === "object") {
    return b.response as Record<string, unknown>;
  }
  if (b.data && typeof b.data === "object") {
    return b.data as Record<string, unknown>;
  }
  return undefined;
}

function readApiMessage(body: unknown): string {
  const top = readTopMsg(body);
  if (top) return top;
  const p = extractResponsePayload(body);
  if (p && typeof p.message === "string" && p.message.trim()) {
    return p.message.trim();
  }
  return "";
}

export type KycSimpleResult =
  | { ok: true; message?: string }
  | { ok: false; message: string };

// Sandbox returns HTTP 200 with e.g. "OTP generated for this aadhaar, please try
// after 45 seconds" when an OTP was already issued — no new SMS goes out.
function readRateLimitMessage(body: unknown): string | undefined {
  const message = readApiMessage(body);
  return /try after/i.test(message) ? message : undefined;
}

export default class KycAPI {
  private readonly client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: process.env.REACT_APP_STORE_API_URL,
      timeout: 30000,
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
    });

    this.client.interceptors.request.use((config) => {
      const params =
        config.data instanceof URLSearchParams
          ? config.data
          : new URLSearchParams();
      appendAuth(params);
      config.data = params;
      return config;
    });
  }

  async sendOtp(aadhaarNumber: string): Promise<KycSimpleResult> {
    const params = new URLSearchParams();
    params.append("aadhaar_number", aadhaarNumber);

    try {
      const res = await this.client.post<KycEnvelope>("/v1/kyc/send-otp", params);
      const body = res.data as KycEnvelope;

      if (body.status === "failed" || (body.code !== undefined && body.code >= 400)) {
        return { ok: false, message: readApiMessage(body) || "Could not send OTP." };
      }

      if (body.status === "success" && (body.code === undefined || body.code === 200)) {
        return { ok: true, message: readRateLimitMessage(body) };
      }

      if (body.code === 200) {
        return { ok: true, message: readRateLimitMessage(body) };
      }

      return { ok: false, message: readApiMessage(body) || "Could not send OTP." };
    } catch (e: unknown) {
      const ax = e as { response?: { data?: unknown } };
      return {
        ok: false,
        message: readApiMessage(ax.response?.data) || "Request failed. Please try again.",
      };
    }
  }

  async verifyOtp(otp: string): Promise<KycSimpleResult> {
    const params = new URLSearchParams();
    params.append("otp", otp);

    try {
      const res = await this.client.post<KycEnvelope>("/v1/kyc/verify-otp", params);
      const body = res.data as unknown;
      const payload = extractResponsePayload(body);

      if (payload && String(payload.kyc_status || "") === "success") {
        return { ok: true };
      }

      const envelope = body as KycEnvelope;
      if (envelope.status === "failed" || (envelope.code !== undefined && envelope.code >= 400)) {
        return { ok: false, message: readApiMessage(body) || "Verification failed." };
      }

      return { ok: false, message: readApiMessage(body) || "Verification failed." };
    } catch (e: unknown) {
      const ax = e as { response?: { data?: unknown } };
      return {
        ok: false,
        message: readApiMessage(ax.response?.data) || "Request failed. Please try again.",
      };
    }
  }
}
